/**
 * Ruolo simulato dell'ambiente demo.
 *
 * Esiste solo quando il bypass di autenticazione è attivo. A bypass spento ogni
 * funzione qui dentro risolve a `unauthorized` e non consulta né la query string
 * né localStorage: il chiamante non deve fidarsi del flag, ce ne occupiamo qui.
 */
import apiClient from '@/services/api'
import { PERMISSIONS, canCheck, type Feature } from '@/config/permissions'
import { UserRole } from '@/types'

interface DemoCustomRole {
  permissions: string[]
  write_groups: Record<string, string[]>
}

/**
 * I ruoli personalizzati dell'istanza demo, letti una volta all'avvio.
 *
 * Senza questo il selettore conosce solo i quattro ruoli fissi, e i gruppi di
 * campi non si possono vedere all'opera: erano verificabili solo dai test.
 * Il registro resta vuoto a bypass spento, cosi' `validate` continua a chiudere
 * su qualunque nome non fisso.
 */
let customRoles: Record<string, DemoCustomRole> = {}

export const BYPASS_MODE = import.meta.env.VITE_AUTH_BYPASS === 'true'

export const DEMO_ROLE_STORAGE_KEY = 'sm-demo-role'

const KNOWN_ROLES: string[] = Object.values(UserRole)

/** Un valore non riconosciuto non apre niente: chiude. */
const validate = (value: string | null | undefined): string => {
  if (!value) return UserRole.UNAUTHORIZED
  if (KNOWN_ROLES.includes(value)) return value
  // Un nome personalizzato vale solo se esiste davvero in questa istanza.
  return value in customRoles ? value : UserRole.UNAUTHORIZED
}

/**
 * Legge i ruoli personalizzati dall'API della demo.
 *
 * Va chiamata prima di montare l'applicazione: `resolveDemoRole()` e' sincrona,
 * e un registro ancora vuoto farebbe ricadere su `unauthorized` un nome valido.
 */
export async function loadDemoCustomRoles(): Promise<void> {
  if (!BYPASS_MODE) return
  try {
    const response = await apiClient.get('/admin/roles')
    customRoles = Object.fromEntries(
      (response.data?.roles ?? []).map((role: any) => [
        role.name,
        { permissions: role.permissions ?? [], write_groups: role.write_groups ?? {} }
      ])
    )
  } catch {
    // Senza registro restano i quattro ruoli fissi: si degrada, non si rompe.
    customRoles = {}
  }
}

/** I ruoli che il selettore puo' offrire: i quattro fissi piu' i personalizzati. */
export function listDemoRoles(): string[] {
  return [...KNOWN_ROLES, ...Object.keys(customRoles)]
}

export function resolveDemoRole(): string {
  if (!BYPASS_MODE) return UserRole.UNAUTHORIZED

  const fromQuery = new URLSearchParams(window.location.search).get('demoRole')
  if (fromQuery) return validate(fromQuery)

  let fromStorage: string | null = null
  try {
    fromStorage = localStorage.getItem(DEMO_ROLE_STORAGE_KEY)
  } catch {
    // navigazione privata o storage bloccato: si passa alla sorgente successiva
  }
  if (fromStorage) return validate(fromStorage)

  const fromEnv = import.meta.env.VITE_DEMO_ROLE
  if (fromEnv) return validate(fromEnv)

  return UserRole.ADMIN
}

export function setDemoRole(role: string): void {
  if (!BYPASS_MODE) return
  try {
    localStorage.setItem(DEMO_ROLE_STORAGE_KEY, role)
  } catch {
    // il ruolo resta applicato per questa pagina, non sopravvive al reload
  }
}

/**
 * Permessi coerenti con il ruolo demo.
 *
 * Serve perché `usePermissions.canAccess()` consulta la cache dei permessi per i
 * ruoli custom e la mappa PERMISSIONS per quelli fissi: senza questo filtro il
 * bypass concederebbe tutto e le due strade darebbero risposte diverse.
 */
export function demoPermissions(role: string): string[] {
  const custom = customRoles[role]
  if (custom) return custom.permissions
  return (Object.keys(PERMISSIONS) as Feature[]).filter((feature) =>
    role === UserRole.ADMIN ? true : canCheck(feature, role as UserRole)
  )
}

/**
 * I gruppi di campi scrivibili del ruolo demo.
 *
 * `null` per un ruolo fisso, che non e' limitato per campo: un dizionario vuoto
 * direbbe l'opposto, cioe' che non puo' scrivere niente.
 */
export function demoWriteGroups(role: string): Record<string, string[]> | null {
  return customRoles[role]?.write_groups ?? null
}
